import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { ShoppingProvider } from './contexts/ShoppingContext';
import { LanguageProvider } from './contexts/LanguageContext';

// Public Pages
import PublicLayout from './layouts/PublicLayout';
import HomePage from './pages/public/HomePage';
import GalleryPage from './pages/public/GalleryPage';
import ServicesPage from './pages/public/ServicesPage';
import ContactPage from './pages/public/ContactPage';
import AcademyPage from './pages/public/AcademyPage';
import StorePage from './pages/public/StorePage';
import AboutPage from './pages/public/AboutPage';
import VerificationPage from './pages/public/VerificationPage';
// Auth Pages
import Login from './components/auth/Login';
import SignUp from './components/auth/SignUp';
import ForgotPassword from './components/auth/ForgotPassword';
import EmailVerification from './components/auth/EmailVerification';

// Protected Routes & Components
import ProtectedRoute from './components/auth/ProtectedRoute';
import ScrollToTop from './components/layout/ScrollToTop';
import Dashboard from './components/dashboard/Dashboard';

// Role-specific Dashboards
import AdminDashboard from './components/dashboard/admin/AdminDashboard';
import StudentDashboard from './components/dashboard/student/StudentDashboard';
import InstructorDashboard from './components/dashboard/instructor/InstructorDashboard';

// Course Management
import CoursesPage from './components/courses/CoursesPage';
import AddCourse from './components/courses/AddCourse';
import CourseDetails from './components/courses/CourseDetails';
import EditCourse from './components/courses/EditCourse';
import StudentCourses from './components/student/StudentCourses';
import StudentCourseDetails from './components/student/CourseDetails';
import StudentCourseCatalog from './components/student/StudentCourseCatalog';
import LessonReaderPage from './components/lessons/LessonReaderPage';

// Student Features
import StudentAssignments from './components/student/StudentAssignments';
import StudentGrades from './components/student/StudentGrades';
import StudentIdCardPage from './components/student/StudentIdCardPage';
import ReceptionKioskPage from './components/reception/ReceptionKioskPage';
import StudentQrResolverPage from './pages/public/StudentQrResolverPage';
import StudentPortfolioPage from './pages/public/StudentPortfolioPage';

// Admin Features
import UsersPage from './components/users/UsersPage';
import InstructorsPage from './components/instructors/InstructorsPage';
import StudentsPage from './components/students/StudentsPage';
import EnrollmentManagement from './components/admin/EnrollmentManagement';
import LessonPlanner from './components/admin/LessonPlanner';
import AddStudent from './components/AddStudent/AddStudent';
import VerificationManagement from './components/admin/VerificationManagement';
import BatchStudentManagement from './components/admin/BatchStudentManagement';
import StaffLetterReview from './components/staff/StaffLetterReview';

// Common Features
import EditProfile from './components/profile/EditProfile';
import PaymentsPage from './components/payments/PaymentsPage';
import SettingsPage from './components/settings/SettingsPage';
import SchedulePage from './components/schedule/SchedulePage';

function App() {
  return (
    <AuthProvider>
      <ShoppingProvider>
        <SidebarProvider>
          <LanguageProvider>
          <Router>
            <ScrollToTop />
            <Routes>
              {/* Public Routes should come first */}
              <Route element={<PublicLayout />}>
                <Route index element={<HomePage />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/academy" element={<AcademyPage />} />
                <Route path="/store" element={<StorePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/verification" element={<VerificationPage />} />
              </Route>

              {/* Auth Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/student-pass/:academyStudentId"
                element={<StudentQrResolverPage />}
              />
              <Route
                path="/students/portfolio/:academyStudentId"
                element={<StudentPortfolioPage />}
              />
              <Route
                path="/verify-email"
                element={
                  <ProtectedRoute
                    requireVerification={false}
                    showLayout={false}
                  >
                    <EmailVerification />
                  </ProtectedRoute>
                }
              />

              {/* Protected Routes should come after public routes */}
              <Route
                path="/dashboard/*"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />

              {/* Other protected routes... */}

              {/* Course Routes */}
              <Route
                path="/courses"
                element={<Navigate to="/courses/available" replace />}
              />
              <Route
                path="/courses/available"
                element={
                  <ProtectedRoute>
                    {({ currentUser }) =>
                      currentUser.role === 'student' ? (
                        <StudentCourseCatalog />
                      ) : (
                        <CoursesPage />
                      )
                    }
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses/my-courses"
                element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <StudentCourses />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses/my-courses/:courseId"
                element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <StudentCourseDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses/my-courses/:courseId/lessons/:lessonId"
                element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <LessonReaderPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses/:courseId/lessons/:lessonId"
                element={
                  <ProtectedRoute>
                    <LessonReaderPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses/add"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <AddCourse />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses/edit/:id"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <EditCourse />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses/:id"
                element={
                  <ProtectedRoute>
                    {({ currentUser }) =>
                      currentUser.role === 'student' ? (
                        <StudentCourseDetails />
                      ) : (
                        <CourseDetails />
                      )
                    }
                  </ProtectedRoute>
                }
              />

              {/* Role-Based Dashboards */}
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/instructor/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['instructor', 'demonstrator']}>
                    <InstructorDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/demonstrator/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['demonstrator']}>
                    <InstructorDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['manager']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/student/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <StudentDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'staff', 'manager']}>
                    <ReceptionKioskPage />
                  </ProtectedRoute>
                }
              />

              {/* Student Features */}
              <Route
                path="/student-id"
                element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <StudentIdCardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assignments"
                element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <StudentAssignments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/grades"
                element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <StudentGrades />
                  </ProtectedRoute>
                }
              />
              {/* Admin Features */}
              <Route
                path="/users"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/instructors"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <InstructorsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/students"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <StudentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/enrollments"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <EnrollmentManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/batches"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <BatchStudentManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lesson-planner"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager', 'instructor', 'demonstrator']}>
                    <LessonPlanner />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/add-student"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'manager']}>
                    <AddStudent />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/verifications"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <VerificationManagement />
                  </ProtectedRoute>
                }
              />

              {/* Common Features */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <EditProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments"
                element={
                  <ProtectedRoute>
                    <PaymentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/schedule"
                element={
                  <ProtectedRoute>
                    <SchedulePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/staff/letters/:letterId"
                element={
                  <ProtectedRoute
                    allowedRoles={['admin', 'manager', 'instructor', 'demonstrator', 'staff']}
                  >
                    <StaffLetterReview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reception/kiosk"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'staff', 'manager']}>
                    <ReceptionKioskPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reception/kiosk/:academyStudentId"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'staff', 'manager']}>
                    <ReceptionKioskPage />
                  </ProtectedRoute>
                }
              />

              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Router>
          </LanguageProvider>
        </SidebarProvider>
      </ShoppingProvider>
    </AuthProvider>
  );
}

export default App;
